/**
 * A régua da esteira do caso — regra pura, sem I/O (E91.F1, GUS-51 RF02).
 *
 * Saiu de `CaseStageStepper.tsx` porque o componente passou a responder três
 * perguntas com três fontes (rota, evidência, pré-condição) e o arquivo
 * estourava o teto de componente. A regra vive na lib, onde é testável sem
 * renderizar; o componente só desenha o que ela decide.
 */
import type { Modalidade, WorkflowStatus } from '@/lib/cases';
import { podeIrParaMesaEletronica } from '@/lib/caso_destino';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';

export type CaseStage = 'criacao' | 'preliminar' | 'mesa' | 'laudo';
export type ActiveStage = 'preliminar' | 'mesa' | 'laudo';
/**
 * `available` (E91.F1): dá para ir, mas ainda não foi concluída. Antes só
 * existiam três estados, e "navegável" era sinônimo de "concluída ou atual" —
 * o que obrigava a esteira a mentir num dos dois sentidos.
 */
export type StageState = 'done' | 'current' | 'available' | 'upcoming';

/** O que a raia eletrônica já sabe do caso, e de onde a esteira lê a evidência. */
export interface EvidenciasEletronicas {
  /** As peças vivas da raia (`GET /exame-eletronico`). */
  pecas: readonly ExameEletronicoCcbData[];
}

const STAGE_ORDER: readonly CaseStage[] = ['criacao', 'preliminar', 'mesa', 'laudo'];

/**
 * E87.F2 (GUS-31 CA01) deu à raia eletrônica um degrau "Análise fotográfica" entre o
 * carregamento dos documentos e a pré-análise. E123.F3 (GUS-128, ADR-0079 E.24): o degrau saiu —
 * o sistema elege as fotos e faz a conferência facial sozinho, e o resultado aparece na
 * pré-análise, com "Trocar fotos". As duas raias voltaram a ter a mesma esteira.
 */
export function ordemDaEsteira(): readonly CaseStage[] {
  return STAGE_ORDER;
}

/**
 * Etapa fixada pelo próprio `workflow_status`, quando ele a determina
 * (raia grafotécnica). `encerrado_*` (E72.F1) é terminal na MESMA etapa.
 */
const ETAPA_PELO_STATUS: Partial<Record<NonNullable<WorkflowStatus>, CaseStage>> = {
  rascunho: 'criacao',
  encerrado_triagem: 'preliminar',
  encerrado_com_relatorio: 'preliminar',
  laudo_em_elaboracao: 'laudo',
  eletronica_concluida: 'laudo',
  aguardando_revisor: 'laudo',
  devolvido_pelo_revisor: 'laudo',
  finalizado: 'laudo',
  assinado: 'laudo',
};

/** Índice da etapa mais avançada ALCANÇADA pelo caso (grafotécnica). */
function reachedIndex(
  status: WorkflowStatus | null | undefined,
  activeStage: ActiveStage,
  ordem: readonly CaseStage[],
): number {
  const fixada = status == null ? undefined : ETAPA_PELO_STATUS[status];
  if (fixada !== undefined) return ordem.indexOf(fixada);
  // `em_analise` não distingue triagem de mesa: o contexto da página resolve.
  return ordem.indexOf(activeStage);
}

function estadosGrafotecnicos(
  status: WorkflowStatus | null | undefined,
  activeStage: ActiveStage,
): Record<CaseStage, StageState> {
  const ordem = STAGE_ORDER;
  const reached = reachedIndex(status, activeStage, ordem);
  const terminal =
    status === 'finalizado' ||
    status === 'assinado' ||
    status === 'encerrado_triagem' ||
    status === 'encerrado_com_relatorio';
  const entries = ordem.map((stage, i): [CaseStage, StageState] => {
    if (i < reached) return [stage, 'done'];
    if (i === reached) return [stage, terminal ? 'done' : 'current'];
    return [stage, 'upcoming'];
  });
  return Object.fromEntries(entries) as Record<CaseStage, StageState>;
}

/**
 * Status que o servidor só deriva depois de o caso ter chegado ao laudo
 * (E78.F1, E79.F1). Valem como evidência de que preliminar e mesa ficaram
 * para trás — mesmo quando a página não recebeu as peças.
 */
const STATUS_DE_LAUDO: ReadonlySet<string> = new Set([
  'laudo_em_elaboracao',
  'eletronica_concluida',
  'aguardando_revisor',
  'devolvido_pelo_revisor',
  'finalizado',
  'assinado',
]);

const SEM_EVIDENCIAS: EvidenciasEletronicas = { pecas: [] };

interface LeituraDasEvidencias {
  temDocumentos: boolean;
  algumaVerificada: boolean;
  todasConcluidas: boolean;
  emLaudo: boolean;
  status: WorkflowStatus | null | undefined;
}

function lerEvidencias(
  status: WorkflowStatus | null | undefined,
  evidencias: EvidenciasEletronicas,
): LeituraDasEvidencias {
  const { pecas } = evidencias;
  const temDocumentos = pecas.length > 0;
  return {
    status,
    temDocumentos,
    algumaVerificada: podeIrParaMesaEletronica(pecas),
    todasConcluidas: temDocumentos && pecas.every((p) => p.exame?.status === 'concluido'),
    emLaudo: status != null && STATUS_DE_LAUDO.has(status),
  };
}

/** Conclusão por EVIDÊNCIA persistida — nunca porque a etapa seguinte abriu. */
function concluidas(l: LeituraDasEvidencias): Record<CaseStage, boolean> {
  return {
    // O caso eletrônico só sai de `rascunho` na primeira verificação: o que
    // conclui a criação é ter documentos, não o status.
    criacao: l.temDocumentos || (l.status != null && l.status !== 'rascunho'),
    preliminar: l.algumaVerificada || l.emLaudo,
    mesa: l.todasConcluidas || l.emLaudo,
    laudo: l.status === 'finalizado' || l.status === 'assinado',
  };
}

/** Disponibilidade por PRÉ-CONDIÇÃO. */
function disponiveis(l: LeituraDasEvidencias): Record<CaseStage, boolean> {
  // Peça verificada ou status de laudo provam que houve documentos, mesmo
  // quando a página não recebeu a lista (E79.F1).
  const houveDocumentos = l.temDocumentos || l.algumaVerificada || l.emLaudo;
  return {
    criacao: true,
    preliminar: houveDocumentos,
    mesa: l.algumaVerificada || l.emLaudo,
    laudo: l.todasConcluidas || l.emLaudo,
  };
}

function estadosEletronicos(
  status: WorkflowStatus | null | undefined,
  activeStage: ActiveStage,
  evidencias: EvidenciasEletronicas,
): Record<CaseStage, StageState> {
  const leitura = lerEvidencias(status, evidencias);
  const concluida = concluidas(leitura);
  const disponivel = disponiveis(leitura);
  const entries = STAGE_ORDER.map((stage): [CaseStage, StageState] => {
    if (stage === activeStage) return [stage, 'current'];
    if (concluida[stage]) return [stage, 'done'];
    return [stage, disponivel[stage] ? 'available' : 'upcoming'];
  });
  return Object.fromEntries(entries) as Record<CaseStage, StageState>;
}

export interface OpcoesDaEsteira {
  /** E74.F1 — omitida = grafotécnica (rotas originais). */
  modalidade?: Modalidade | null | undefined;
  /** E91.F1 — só a raia eletrônica passa; sem elas, nada está concluído nem disponível. */
  evidencias?: EvidenciasEletronicas | undefined;
}

/** Estado de cada etapa. Grafotécnica pelo status; eletrônica pelas evidências. */
export function caseStageStates(
  status: WorkflowStatus | null | undefined,
  activeStage: ActiveStage,
  opcoes: OpcoesDaEsteira = {},
): Partial<Record<CaseStage, StageState>> {
  if (opcoes.modalidade === 'eletronica') {
    return estadosEletronicos(status, activeStage, opcoes.evidencias ?? SEM_EVIDENCIAS);
  }
  return estadosGrafotecnicos(status, activeStage);
}
